import { BellOutlined, LoadingOutlined, MailOutlined } from "@ant-design/icons";
import { Button, Empty, Table, Typography } from "antd";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TableHelper } from "../../helpers/TableHelper";
import { translate } from "../../locale";
import UserServices from "../../services/UserServices";
import CellButton from "../Buttons/CellButton";
import SendEmail from "../Modals/SendEmail";
import SendNotification from "../Modals/SendNotification";

const { Text } = Typography;

const antIcon = (
  <LoadingOutlined
    style={{
      fontSize: 24,
    }}
    spin
  />
);

const BehindUsers = ({ selectedData }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [selecteUser, setSelecteUser] = useState(null);
  const [showEmail, setShowEmail] = useState(false);
  const [tableParams, setTableParams] = useState({
    pagination: {
      current: 1,
      pageSize: 10,
      total: 0,
    },
  });
  const [total, setTotal] = useState(0);

  const navigate = useNavigate();

  const renderInfo = (title, value, unit = "días") => (
    <div>
      {translate(title)} <Text className="text-custom_red">{value}</Text>{" "}
      {translate(unit)}
    </div>
  );

  const columns = [
    {
      title: "",
      dataIndex: "actions",
      key: "actions",
      render: (_, record) => (
        <div className="flex space-x-2">
          <Button
            type="link"
            size="small"
            onClick={() => {
              handleSendNotification(record.username);
            }}
          >
            <BellOutlined className="text-black" />
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              handleSendEmail(record.username);
            }}
          >
            <MailOutlined className="text-black" />
          </Button>
        </div>
      ),
      fixed: "left",
    },
    {
      title: "Apellido",
      dataIndex: "surname",
      key: "surname",
      render: (_, record) => (
        <CellButton
          onClick={() => navigate(`/admin/buscar-paciente/${record.username}`)}
          className="text-black hover:text-custom_primary"
        >
          {record.surname}
        </CellButton>
      ),
      sorter: { multiple: 1 },
      fixed: "left",
    },
    {
      title: "Nombre",
      dataIndex: "name",
      key: "name",
      render: (_, record) => (
        <CellButton
          onClick={() => navigate(`/admin/buscar-paciente/${record.username}`)}
          className="text-black hover:text-custom_primary"
        >
          {record.name}
        </CellButton>
      ),
      sorter: { multiple: 2 },
    },
    {
      title: "Registros atrasados",
      dataIndex: "inactive_days",
      key: "inactive_days",
      render: (_, record) => {
        return (
          <>
            {record.last_control > 0 &&
              renderInfo("Automedición", record.last_control)}
            {record.last_feeding > 0 &&
              renderInfo("Alimentación", record.last_feeding)}
            {record.last_mt > 0 && renderInfo("Medicamento", record.last_mt)}
            {record.last_survey > 0 &&
              renderInfo("Autoevaluación", record.last_survey)}
          </>
        );
      },
      sorter: { multiple: 3 },
    },
    {
      title: "Última medición",
      dataIndex: "last_control",
      key: "last_control",
      render: (_, record) => {
        return (
          <>
            {record.last_control > 0 &&
              renderInfo("Automedición", record.last_control)}
            {record.last_mt > 0 && renderInfo("Medicamento", record.last_mt)}
            {record.last_survey > 0 &&
              renderInfo("Autoevaluación", record.last_survey)}
          </>
        );
      },
    },
  ];

  useEffect(() => {
    if (selectedData !== null) {
      let data = {
        page_size: tableParams.pagination.pageSize,
        page_num: tableParams.pagination.current,
      };
      const sorter = TableHelper.sorter(tableParams);
      if (sorter !== null) {
        data = { ...data, order_by: sorter };
      }
      loadData(data);
    }
  }, [selectedData, JSON.stringify(tableParams)]);

  const loadData = async (data) => {
    setLoading(true);
    try {
      const response = await UserServices.getBehindUser(
        selectedData.period,
        selectedData.suscription,
        null,
        data
      );
      if (response.status >= 200 && response.status < 300) {
        setData(response.data.inactives);
        setTotal(response.data.total_pages * tableParams.pagination.pageSize);
      }
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const handleSendNotification = (username) => {
    setSelecteUser(username);
    setShowNotification(true);
  };

  const handleSendEmail = (username) => {
    setSelecteUser(username);
    setShowEmail(true);
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      filters,
      sorter,
    });

    // `dataSource` is useless since `pageSize` changed
    if (pagination.pageSize !== tableParams.pagination?.pageSize) {
      setData([]);
    }
  };

  return (
    <>
      <Table
        rowKey={(i) => i.username}
        dataSource={data}
        columns={columns}
        loading={
          loading
            ? { indicator: antIcon, className: "text-custom_blue" }
            : false
        }
        locale={{
          emptyText: <Empty description={translate("No hay datos")} />,
          sortTitle: translate("Ordenar"),
          triggerDesc: translate("Ordenar (descendente)"),
          triggerAsc: translate("Ordenar (ascendente)"),
          cancelSort: translate("Cancelar orden"),
        }}
        pagination={{ ...tableParams.pagination, total: total }}
        onChange={handleTableChange}
      />
      <SendNotification
        patient={selecteUser}
        visible={showNotification}
        onCancel={() => setShowNotification(false)}
        onComplete={() => setShowNotification(false)}
      />
      <SendEmail
        patient={selecteUser}
        visible={showEmail}
        onCancel={() => setShowEmail(false)}
        onComplete={() => setShowEmail(false)}
      />
    </>
  );
};

BehindUsers.propTypes = {
  selectedData: PropTypes.object.isRequired,
};

export default BehindUsers;
